import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { MsalService } from "@azure/msal-angular";
import { Observable, Subject, of, from, firstValueFrom, defer } from "rxjs";
import { mapTo, catchError, finalize, tap } from "rxjs/operators";
import { Tokens } from "../models/tokens.model";
import { UserIdentity } from "../models/userIdentity.model";
import { EndpointService } from "./endpoint.service";
import { LoginResponse } from "../models/loginResponse.model";
import { SecurityProfileEnum } from "../enums/securityProfile.enum";
import { UserPreferences } from "../models/userPreferences.model";
import { DataAnalysisRequestModel } from "../models/dataAnalysisRequest.model";
import { DateFormatService } from "./dateFormat.service";
import { UserProfile } from "../models/userProfile.model";
import { LanguageEnum } from "../enums/language.enum";
import { environment } from "../../environments/environment";
import { LocalStorageConstants } from "../shared/constants/localStorage.constants";
import { JwtClaims } from "../models/jwtClaims.model";
import { decodeJwtClaims } from "../_helpers/jwt.helper";
import { StartViewAsRequest, StopViewAsRequest, ViewAsSelection } from "../models/viewAs.model";

declare let pendo: any;
@Injectable({
    providedIn: "root",
})
export class AuthService extends EndpointService {
    private static readonly REFRESH_LOCK_NAME = 'leap-auth-refresh' as const;

    refreshTimezone = new Subject<any>();
    private readonly aiAnalystEnabledChanged = new Subject<void>();
    private readonly viewAsEnded = new Subject<void>();
    isPendoInitialized: boolean = false;
    private claimsCache: { token: string | null; claims: JwtClaims | null } = { token: null, claims: null };

    constructor(
        http: HttpClient,
        private dateFormatService: DateFormatService,
        private msalService: MsalService,
    ) {
        super(http);
        this.migrateLegacyLocalStorageKeys();
    }

    get aiAnalystEnabledChanged$(): Observable<void> {
        return this.aiAnalystEnabledChanged.asObservable();
    }

    // Emits when a token refresh came back without the role preview the previous token carried
    // (the backend re-validates the preview on every refresh and drops it when no longer allowed).
    get viewAsEnded$(): Observable<void> {
        return this.viewAsEnded.asObservable();
    }

    private migrateLegacyLocalStorageKeys(): void {
        const legacyKeys: Record<string, string> = {
            selectedOrg: LocalStorageConstants.selectedOrg,
            selectedRisk: LocalStorageConstants.selectedRisk,
            selectedParameter: LocalStorageConstants.selectedParameter,
            dateRange: LocalStorageConstants.selectedDateRange,
            deviceName: LocalStorageConstants.deviceName,
        };
        Object.entries(legacyKeys).forEach(([oldKey, newKey]) => {
            const value = localStorage.getItem(oldKey);
            if (value !== null && localStorage.getItem(newKey) === null) {
                localStorage.setItem(newKey, value);
                localStorage.removeItem(oldKey);
            }
        });
    }

    login(username: string, password: string) {
        return this.http
            .post<LoginResponse>(this.getApiUrl("Auth/Login"), {
                username,
                password,
            })
            .pipe(tap((response) => this.handleLoginResponse(response)));
    }
    googleLogin(payload: any) {
        return this.http
            .post<LoginResponse>(this.getApiUrl("Auth/GoogleLogin"), payload)
            .pipe(tap((response) => this.handleLoginResponse(response)));
    }

    microsoftLogin(payload: any) {
        return this.http
            .post<LoginResponse>(this.getApiUrl("Auth/MicrosoftLogin"), payload)
            .pipe(tap((response) => this.handleLoginResponse(response)));
    }

    getZendeskSsoUrl(): Observable<string> {
        return this.http.get<string>(this.getApiUrl("Auth/GetZendeskSsoUrl"));
    }

    logout(): Observable<boolean> {
        return this.http
            .post<any>(this.getApiUrl("Auth/Logout"), {
                refreshToken: this.getRefreshToken(),
            })
            .pipe(
                mapTo(true),
                catchError((error) => {
                    return of(false);
                }),
                finalize(() => this.doLogoutUser()),
            );
    }

    refreshToken(): Observable<Tokens | null> {
        return from(this.refreshTokenWithCrossTabLock()).pipe(
            catchError(() => of(null)),
        );
    }

    private async refreshTokenWithCrossTabLock(): Promise<Tokens | null> {
        const refreshTokenAtEntry = this.getRefreshToken();

        return this.withRefreshLock(async () => {
            const currentAccess = this.getAccessToken();
            const currentRefresh = this.getRefreshToken();

            if (
                currentAccess &&
                currentRefresh &&
                currentRefresh !== refreshTokenAtEntry
            ) {
                const tokens = new Tokens();
                tokens.accessToken = currentAccess;
                tokens.refreshToken = currentRefresh;
                return tokens;
            }

            return this.performRefresh();
        });
    }

    private async performRefresh(): Promise<Tokens | null> {
        try {
            const wasViewAsActive = this.isViewAsActive();
            const sentRefreshToken = this.getRefreshToken();
            const response = await firstValueFrom(
                this.http.post<LoginResponse>(this.getApiUrl("Auth/RefreshToken"), {
                    accessToken: this.getAccessToken(),
                    refreshToken: sentRefreshToken,
                }),
            );
            // Another tab stored newer tokens while this request was in flight; storing this older
            // response over them would undo that tab's change, so the caller adopts the stored ones.
            if (this.getRefreshToken() !== sentRefreshToken) {
                return null;
            }
            const tokens = this.mapToTokens(response);
            this.storeTokens(tokens);
            if (tokens && wasViewAsActive && !this.isViewAsActive()) {
                this.storeEffectiveAccess(response);
                this.viewAsEnded.next();
            }
            return tokens;
        } catch {
            return null;
        }
    }

    revokeTokens(expectedAccessToken?: string) {
        return this.http
            .post<any>(this.getApiUrl("Auth/RevokeTokens"), {
                accessToken: this.getAccessToken(),
                refreshToken: this.getRefreshToken(),
            })
            .pipe(
                mapTo(true),
                tap((_) => this.doLogoutUser(expectedAccessToken)),
                catchError((error) => {
                    this.doLogoutUser(expectedAccessToken);
                    return of(false);
                }),
            );
    }

    impersonateUser(userId: string) {
        return this.http
            .post<LoginResponse>(this.getApiUrl("Auth/ImpersonateUser"), {
                userId,
            })
            .pipe(tap((response) => this.handleLoginResponse(response, true)));
    }

    startViewAs(selection: ViewAsSelection): Observable<LoginResponse> {
        return this.rotateViewAsTokens(() => {
            const request: StartViewAsRequest = {
                roleId: selection.roleId,
                refreshToken: this.getRefreshToken(),
            };
            if (selection.organizationLevelIds.length) {
                request.organizationLevelIds = selection.organizationLevelIds;
            }
            return this.http
                .post<LoginResponse>(this.getApiUrl("Auth/StartViewAs"), request)
                .pipe(tap((response) => this.handleViewAsResponse(response)));
        });
    }

    stopViewAs(): Observable<LoginResponse> {
        return this.rotateViewAsTokens(() => {
            const request: StopViewAsRequest = { refreshToken: this.getRefreshToken() };
            return this.http
                .post<LoginResponse>(this.getApiUrl("Auth/StopViewAs"), request)
                .pipe(tap((response) => this.handleViewAsResponse(response)));
        });
    }

    // Start and Stop replace the tokens every tab shares, so they hold the refresh lock: a refresh in
    // another tab waits, then adopts their tokens instead of storing its own response over them.
    // This is the one place outside the login flow that handles a 401 itself, on purpose: TokenInterceptor's
    // refresh would wait on the lock these calls already hold and freeze every tab (hence their TOKEN_ENDPOINTS
    // entries), so they refresh here and resend once with the new refresh token.
    private rotateViewAsTokens(send: () => Observable<LoginResponse>): Observable<LoginResponse> {
        const sendRefreshingOnce = async (): Promise<LoginResponse> => {
            try {
                return await firstValueFrom(send());
            } catch (error: unknown) {
                if (!(error instanceof HttpErrorResponse) || error.status !== HttpStatusCode.Unauthorized) {
                    throw error;
                }
                if (!(await this.performRefresh())) {
                    throw error;
                }
                return firstValueFrom(send());
            }
        };
        return defer(() => this.withRefreshLock(sendRefreshingOnce));
    }

    // Everything that rotates the shared tokens runs through here, so they all hold the same cross-tab lock.
    // Browsers without navigator.locks run the work directly.
    private withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
        return navigator.locks?.request
            ? navigator.locks.request(AuthService.REFRESH_LOCK_NAME, work)
            : work();
    }

    // The preview lives only in the access token, so these read its claims rather than any stored flag.
    isViewAsActive(): boolean {
        return isTrueClaim(this.getAccessTokenClaims()?.viewAsRole);
    }

    isReadonlyContext(): boolean {
        return isTrueClaim(this.getAccessTokenClaims()?.isReadonlyContext);
    }

    getViewAsState(): ViewAsSelection | null {
        const claims = this.getAccessTokenClaims();
        if (!claims || !isTrueClaim(claims.viewAsRole) || !claims.assumedRoleId) {
            return null;
        }
        const orgLevelIds = claims.assumedOrganizationLevelIds;
        return {
            roleId: claims.assumedRoleId,
            organizationLevelIds: Array.isArray(orgLevelIds) ? orgLevelIds : orgLevelIds ? [orgLevelIds] : [],
        };
    }

    private getAccessTokenClaims(): JwtClaims | null {
        const token = this.getAccessToken();
        if (token !== this.claimsCache.token) {
            this.claimsCache = { token, claims: decodeJwtClaims(token) };
        }
        return this.claimsCache.claims;
    }

    // Start and Stop return a full login response for the new effective role and org levels.
    private handleViewAsResponse(response: LoginResponse): void {
        this.handleLoginResponse(response, this.getCurrentUser()?.isImpersonated === true);
    }

    // A refresh response describes the new effective access; the rest of the stored identity is unchanged.
    private storeEffectiveAccess(response: LoginResponse): void {
        const currentUser = this.getCurrentUser();
        if (!currentUser) {
            return;
        }
        currentUser.roleId = response.roleId;
        currentUser.organizationLevelIds = response.organizationLevelIds;
        currentUser.organizationLevelPermissions = response.organizationLevelPermissions;
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
    }

    isLoggedIn() {
        if (
            document.readyState === "complete" &&
            this.getCurrentUser() &&
            !this.isPendoInitialized
        ) {
            this.initilizePendo(this.getCurrentUser());
        }
        return !!this.getAccessToken();
    }

    getCurrentUser(): UserIdentity {
        return JSON.parse(
            localStorage.getItem(LocalStorageConstants.userKey),
        ) as UserIdentity;
    }

    setCurrentUserPreferences(preferences: UserPreferences | UserProfile) {
        if (!preferences) {
            return;
        }
        const currentUser = this.getCurrentUser();
        currentUser.preferences.pmUnitId = preferences.pmUnitId;
        currentUser.preferences.temperatureUnitId =
            preferences.temperatureUnitId;
        currentUser.preferences.showMap = preferences.showMap;
        currentUser.preferences.defaultMapLocation =
            preferences.defaultMapLocation;
        currentUser.preferences.gasUnit = preferences.gasUnit;
        currentUser.preferences.windSpeedUnit = preferences.windSpeedUnit;
        currentUser.preferences.rainfallUnit = preferences.rainfallUnit;
        currentUser.preferences.flowRateUnit = preferences.flowRateUnit;
        currentUser.preferences.datePreference = preferences.datePreference;
        currentUser.preferences.timePreference = preferences.timePreference;
        currentUser.preferences.languagePreference =
            preferences.languagePreference;
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
    }

    setCurrentUserParameters(parameters: number[]) {
        if (!parameters || !parameters.length) {
            return;
        }
        const currentUser = this.getCurrentUser();
        currentUser.preferences.parameters = parameters;
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
    }

    setCurrentUserAnalysisParameters(model: DataAnalysisRequestModel) {
        const selectedDevice = model.isViewByEndPoint
            ? `0+${model.endPointId}`
            : `1+${model.sensorId}`;
        if (model.selectedDevices[0] !== selectedDevice) {
            return;
        }
        const currentUser = this.getCurrentUser();
        currentUser.preferences.parameters = model.parameters;
        currentUser.preferences.selectedDevices = model.selectedDevices;
        currentUser.preferences.fromDate =
            this.dateFormatService.convertStringToDate(model.startDate);
        currentUser.preferences.toDate =
            this.dateFormatService.convertStringToDate(model.endDate);
        currentUser.preferences.timeIntervalId = model.timeIntervalId;
        currentUser.preferences.shiftStart =
            this.dateFormatService.convertStringToDate(model.startTime);
        currentUser.preferences.shiftEnd =
            this.dateFormatService.convertStringToDate(model.endTime);
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
    }

    setCurrentUserViewBySensor(isViewBySensor: boolean) {
        const currentUser = this.getCurrentUser();
        currentUser.preferences.isViewBySensor = isViewBySensor;
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
    }

    setCurrentUserAiAnalystEnabled(isAiAnalystEnabled: boolean) {
        const currentUser = this.getCurrentUser();
        currentUser.isAiAnalystEnabled = isAiAnalystEnabled;
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
        this.aiAnalystEnabledChanged.next();
    }

    setCurrentUserTimeZone(timeZone: string) {
        const currentUser = this.getCurrentUser();
        currentUser.userTimeZone = timeZone;
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
        this.refreshTimezone.next(true);
    }

    setCurrentUserNotificationDetails(
        parameter: number,
        fromDate: Date,
        toDate: Date,
    ) {
        const currentUser = this.getCurrentUser();
        currentUser.preferences.parameters = [parameter];
        currentUser.preferences.fromDate = fromDate;
        currentUser.preferences.toDate = new Date(toDate);
        currentUser.preferences.toDate.setMinutes(
            currentUser.preferences.toDate.getMinutes() + 1,
        );
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
    }

    setCurrentUserEndPoint(endPointId: string) {
        const currentUser = this.getCurrentUser();
        currentUser.preferences.endPointId = endPointId;
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
    }

    setCurrentUserSidebarPinned(isSidebarPinned: boolean) {
        const currentUser = this.getCurrentUser();
        currentUser.preferences.isSidebarPinned = isSidebarPinned;
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(currentUser),
        );
    }

    getAccessToken() {
        return localStorage.getItem(LocalStorageConstants.accessToken);
    }

    getRefreshToken() {
        return localStorage.getItem(LocalStorageConstants.refreshToken);
    }

    getRememberMe() {
        const stored = JSON.parse(
            localStorage.getItem(LocalStorageConstants.rememberMe),
        ) as boolean;

        return stored === true || stored === null;
    }

    setRememberMe(rememberMe: boolean) {
        localStorage.setItem(
            LocalStorageConstants.rememberMe,
            JSON.stringify(rememberMe),
        );
    }

    verifyOtp(username: string, otp: string) {
        return this.http
            .post<LoginResponse>(this.getApiUrl("Auth/VerifyOtp"), {
                username,
                otp,
            })
            .pipe(tap((response) => this.handleLoginResponse(response)));
    }

    private initilizePendo(response: UserIdentity) {
        if (!environment.enablePendo) {
            return;
        }

        try {
            pendo.initialize({
                visitor: {
                    id: response.userId,
                    email: response.userEmail,
                    fullName: response.userFullName,
                },
                account: {
                    id: response.organizationLevelPermissions.tenantId,
                    accountName: response.tenantName,
                },
            });
            pendo.validateInstall();
            this.isPendoInitialized = true;
        } catch {
            // Pendo validateInstall is non-critical (analytics-only) — swallow silently.
        }
    }

    private doLoginUser(
        user: UserIdentity,
        tokens: Tokens,
        response: LoginResponse,
    ) {
        this.initilizePendo(user);
        localStorage.setItem(
            LocalStorageConstants.userKey,
            JSON.stringify(user),
        );
        this.aiAnalystEnabledChanged.next();
        this.storeTokens(tokens);
    }

    private doLogoutUser(expectedAccessToken?: string) {
        if (
            expectedAccessToken &&
            this.getAccessToken() !== expectedAccessToken
        ) {
            return;
        }
        localStorage.setItem(LocalStorageConstants.userKey, null);
        this.aiAnalystEnabledChanged.next();
        localStorage.removeItem(LocalStorageConstants.selectedOrg);
        localStorage.removeItem(LocalStorageConstants.selectedRisk);
        localStorage.removeItem(LocalStorageConstants.selectedParameter);
        localStorage.removeItem(LocalStorageConstants.selectedDateRange);
        localStorage.removeItem(LocalStorageConstants.viewAsLabels);
        localStorage.removeItem(LocalStorageConstants.viewAsEndedNotice);
        this.removeTokens();
        this.msalService.instance.clearCache().catch(() => {});
    }

    private storeTokens(tokens: Tokens) {
        if (tokens === null) {
            return;
        }

        localStorage.setItem(
            LocalStorageConstants.accessToken,
            tokens.accessToken,
        );
        localStorage.setItem(
            LocalStorageConstants.refreshToken,
            tokens.refreshToken,
        );
    }

    private removeTokens() {
        localStorage.removeItem(LocalStorageConstants.accessToken);
        localStorage.removeItem(LocalStorageConstants.refreshToken);
    }

    private handleLoginResponse(
        response: LoginResponse,
        isImpersonated: boolean = false,
    ) {
        const user = this.mapToUserIdentity(response, isImpersonated);
        const tokens = this.mapToTokens(response);

        if (user && user.userType === SecurityProfileEnum.User) {
            const encUserId = window.btoa(response.userId);
            localStorage.setItem(LocalStorageConstants.deviceName, encUserId);
        }

        this.doLoginUser(user, tokens, response);
    }

    private mapToUserIdentity(
        response: LoginResponse,
        isImpersonated: boolean,
    ): UserIdentity {
        if (response && response.userId) {
            const user = new UserIdentity();
            user.userType = response.userType;
            user.userEmail = response.userEmail;
            user.userTimeZone = response.userTimeZone;
            user.organizationLevelPermissions =
                response.organizationLevelPermissions;
            user.tenantLogoUrl = response.tenantLogoUrl;
            user.preferences = response.preferences;
            user.isImpersonated = isImpersonated;
            user.userId = response.userId;
            user.userFullName = response.userFullName;
            user.tenantName = response.tenantName;
            user.roleId = response.roleId;
            user.organizationLevelIds = response.organizationLevelIds;
            user.residency = response.residency;
            user.isAiAnalystEnabled = response.isAiAnalystEnabled;

            return user;
        }

        return null;
    }

    private mapToTokens(response: LoginResponse): Tokens {
        if (response && response.accessToken) {
            const tokens = new Tokens();
            tokens.accessToken = response.accessToken.token;
            tokens.refreshToken = response.refreshToken;

            return tokens;
        }

        return null;
    }

    getLandingPageUrl(): string {
        const permissions = this.getCurrentUser().organizationLevelPermissions;
        if (permissions.canAccessIhModule) {
            return "dashboard/my-dashboard";
        } else if (permissions.canAccessRealTimeDashboard) {
            return "dashboard/real-time-monitoring-dashboard";
        } else {
            return "dashboard/samples";
        }
    }

    getLanguagePreference() {
        const lang = this.getCurrentUser()?.preferences?.languagePreference;
        return lang ?? LanguageEnum.English;
    }
}

// Boolean-typed JWT claims arrive as JSON booleans; accept the string form too.
function isTrueClaim(value: boolean | string | undefined): boolean {
    return value === true || value === "true";
}
